import { useCallback, useState, useEffect, useRef } from 'react';
import { Button } from '@plone/components';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import doesNodeContainClick from '@kitconcept/volto-light-theme/helpers/doesNodeContainClick';
import config from '@plone/volto/registry';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import arrowRightSVG from '@plone/volto/icons/right-key.svg';
import arrowLeftSVG from '@plone/volto/icons/left-key.svg';
import NavigationHeader from './NavigationHeader';
import { NavigationFooter } from './NavigationFooter';
import cx from 'classnames';

type RootItem = {
  url: string;
  title: string;
};

type NavNode = {
  url: string;
  title: string;
  nav_title?: string;
  items?: NavNode[];
  // allow extra fields without breaking typing
  [key: string]: unknown;
};

type LevelData = {
  section: RootItem; // url + title (rootItem at root level)
  items: NavNode[]; // children of the section
  parent: RootItem | null; // null at root; otherwise immediate parent (rootItem for top-level)
};

/**
 * Resolve navigation data for a given index path.
 *
 * @param rootItem - The synthetic "root" section to use at the top level.
 * @param items - The root array of NavNode items.
 * @param currentLevel - The path as indices into arrays: [] | [i] | [i, j] | ...
 * @returns LevelData or null if the path is invalid.
 */
export function getLevelData(
  rootItem: RootItem,
  items: NavNode[],
  currentLevel: number[],
): LevelData | null {
  // Root level: return the whole structure context.
  if (currentLevel.length === 0) {
    return {
      section: rootItem,
      items,
      parent: null,
    };
  }

  // Walk down the path
  let currentItems: NavNode[] = items;
  let parentNode: NavNode | null = null;

  for (let step = 0; step < currentLevel.length; step++) {
    const idx = currentLevel[step];

    if (!Array.isArray(currentItems) || idx < 0 || idx >= currentItems.length) {
      return null; // invalid path
    }

    const node = currentItems[idx];

    // If this is the last step, we can build the LevelData
    const isLast = step === currentLevel.length - 1;
    if (isLast) {
      const section: RootItem = {
        url: node.url,
        title: (node.nav_title as string | undefined) ?? node.title,
      };

      // parent is rootItem for top-level ([i]); otherwise the immediate NavNode parent
      const parent: RootItem | null =
        step === 0
          ? rootItem
          : parentNode
            ? {
                url: parentNode.url,
                title:
                  (parentNode.nav_title as string | undefined) ??
                  parentNode.title,
              }
            : null;

      return {
        section,
        items: Array.isArray(node.items) ? node.items : [],
        parent,
      };
    }

    // go deeper
    parentNode = node;
    currentItems = Array.isArray(node.items) ? node.items : [];
  }

  // Should not be reachable
  return null;
}

const MenuItem = (props) => {
  const { idx, item, handleLinkClicked, displayChildren, pathname } = props;
  const className = props.className || '';
  const items = (displayChildren && item?.items) || [];
  return (
    <li
      className={cx(
        'nav-item',
        item?.url === pathname ? 'current' : '',
        className,
      )}
    >
      <Link
        to={item?.url === '' ? '/' : item?.url}
        className={'enter'}
        onClick={(e) => handleLinkClicked(e, item, idx, displayChildren)}
      >
        <span className="nav-item-title">{item?.title}</span>
        {items?.length > 0 && (
          <Icon name={arrowRightSVG} className="nav-item-icon" />
        )}
      </Link>
    </li>
  );
};

const Navigation = (props) => {
  const { content, isNavMenuOpen, toggleNavMenu, setResetToRoot } = props;
  const history = useHistory();
  const menus = useRef(null);
  const navRoot = useSelector((state) => state.navroot.data || {});
  const items = useSelector((state) => state.navigation.items || []);
  const defaultLevelData = { section: null, items: null, parent: null };
  const [rootItem, setRootItem] = useState(null);
  const [currentLevel, setCurrentLevel] = useState([]);
  const [levelData, setLevelData] = useState(defaultLevelData);
  const navigationSettings = config.settings.dsgov_navigation;

  useEffect(() => {
    setRootItem({ url: navRoot.url, title: navRoot.title });
  }, [navRoot, setRootItem]);

  useEffect(() => {
    setLevelData(getLevelData(rootItem, items, currentLevel));
  }, [items, currentLevel, rootItem]);

  const closeMenus = useCallback(
    (e) => {
      if (e && e.stopPropagation) {
        e.stopPropagation();
      }
      if (isNavMenuOpen) {
        toggleNavMenu();
      }
    },
    [isNavMenuOpen, toggleNavMenu],
  );

  const handleLinkClicked = useCallback(
    (e, levelData, levelId, displayChildren) => {
      if (e !== null && e !== undefined) {
        e.preventDefault();
      }
      if (levelId === -1) {
        const nextLevel = currentLevel.slice(0, -1);
        setCurrentLevel(nextLevel);
      } else if (
        displayChildren &&
        levelId !== null &&
        levelData.items?.length > 0
      ) {
        const nextLevel =
          levelId === -1
            ? currentLevel.slice(0, -1)
            : [...currentLevel, levelId];
        setCurrentLevel(nextLevel);
      } else {
        const url = levelData.url;
        setCurrentLevel([]);
        closeMenus(e);
        history.push(url);
      }
    },
    [history, closeMenus, setCurrentLevel, currentLevel],
  );

  useEffect(() => {
    const closeMenuOnHistoryChange = history.listen(() => {
      closeMenus({});
      setResetToRoot(true);
    });
    return () => {
      closeMenuOnHistoryChange();
    };
  }, [setResetToRoot, history, closeMenus]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isNavMenuOpen) {
        if (menus.current && !doesNodeContainClick(menus.current, e)) {
          closeMenus(e);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside, false);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, [isNavMenuOpen, closeMenus]);

  useEffect(() => {
    return () => {
      const body = document.getElementsByTagName('body')[0];
      if (body.classList.contains('has-menu-open')) {
        body.classList.toggle('has-menu-open');
      }
    };
  }, []);
  return (
    <div className="dsgov-nav" ref={menus}>
      <div className={'nav-container'}>
        <NavigationHeader toggleNavMenu={toggleNavMenu} />
        <div className={`nav-block nav-sections`}>
          <div className="nav-sections-header">
            {levelData?.parent && (
              <Button
                onClick={(e) => handleLinkClicked(e, levelData.parent, -1)}
                className="backButton"
              >
                <Icon name={arrowLeftSVG} size="36px" />
                <span>{levelData.parent.title}</span>
              </Button>
            )}
          </div>

          <div className="nav-sections-body">
            <ul className="sections">
              {levelData?.section?.title && (
                <MenuItem
                  key={levelData.section['@id']}
                  idx={null}
                  item={levelData.section}
                  handleLinkClicked={handleLinkClicked}
                  displayChildren={true}
                  pathname={props.pathname}
                  className={'root'}
                />
              )}
              {levelData?.items?.length > 0 &&
                levelData.items.map((item, idx) => (
                  <MenuItem
                    key={idx}
                    idx={idx}
                    item={item}
                    displayChildren={
                      navigationSettings.doNotDisplayChildren.includes(
                        item['@type'],
                      )
                        ? false
                        : true
                    }
                    handleLinkClicked={handleLinkClicked}
                    pathname={props.pathname}
                  />
                ))}
            </ul>
          </div>
        </div>
        <NavigationFooter content={content} />
      </div>
    </div>
  );
};

export default Navigation;
